import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  KeyRound,
  Loader2,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  UserRoundCheck,
} from 'lucide-react';
import {
  createAIItemKnowledge,
  deleteAIItemKnowledge,
  deleteAIProvider,
  getAccountDetails,
  getAIAccountPolicy,
  getAIHandoffs,
  getAIItemKnowledge,
  getAIProvider,
  getItems,
  resolveAIHandoff,
  saveAIProvider,
  testDeepSeekConnection,
  updateAIAccountPolicy,
  updateAIItemKnowledge,
} from '../services/api';
import {
  AccountDetail,
  AIAccountPolicy,
  AIHandoff,
  AIItemKnowledge,
  AIProviderStatus,
  DeepSeekModel,
  Item,
} from '../types';

const emptyPolicy = (cookieId: string): AIAccountPolicy => ({
  cookie_id: cookieId,
  ai_enabled: false,
  provider: 'deepseek',
  model_name: 'deepseek-v4-flash',
  reply_tone: 'friendly',
  max_reply_chars: 80,
  account_knowledge: '',
  risk_keywords: [],
  max_discount_percent: 10,
  max_discount_amount: 100,
  max_bargain_rounds: 3,
  custom_prompts: '',
});

const cardClass =
  'bg-white rounded-[2rem] border border-gray-100 shadow-sm p-6 md:p-8';
const inputClass =
  'w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition focus:border-black focus:bg-white';

interface AIReceptionProps {
  onPendingCountChange?: (count: number) => void;
}

const AIReception: React.FC<AIReceptionProps> = ({
  onPendingCountChange,
}) => {
  const [provider, setProvider] = useState<AIProviderStatus | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [defaultModel, setDefaultModel] =
    useState<DeepSeekModel>('deepseek-v4-flash');
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [policy, setPolicy] = useState<AIAccountPolicy | null>(null);
  const [knowledge, setKnowledge] = useState<AIItemKnowledge[]>([]);
  const [handoffs, setHandoffs] = useState<AIHandoff[]>([]);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [editingKnowledgeId, setEditingKnowledgeId] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const accountItems = useMemo(
    () => items.filter((item) => item.cookie_id === selectedAccount),
    [items, selectedAccount],
  );

  const run = async (name: string, operation: () => Promise<void>) => {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      await operation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '操作失败，请重试');
    } finally {
      setBusy('');
    }
  };

  const refreshHandoffs = async () => {
    const pending = await getAIHandoffs('pending');
    setHandoffs(pending);
    onPendingCountChange?.(pending.length);
  };

  useEffect(() => {
    Promise.all([
      getAIProvider(),
      getAccountDetails(),
      getItems(),
      getAIHandoffs('pending'),
    ])
      .then(([providerData, accountData, itemData, handoffData]) => {
        setProvider(providerData);
        setDefaultModel(providerData.default_model);
        setAccounts(accountData);
        setItems(itemData);
        setHandoffs(handoffData);
        onPendingCountChange?.(handoffData.length);
        if (accountData[0]) setSelectedAccount(accountData[0].id);
      })
      .catch((caught) => {
        setError(caught instanceof Error ? caught.message : 'AI 接待数据加载失败');
      })
      .finally(() => setLoading(false));
  }, [onPendingCountChange]);

  useEffect(() => {
    if (!selectedAccount) return;
    getAIAccountPolicy(selectedAccount)
      .then(setPolicy)
      .catch(() => setPolicy(emptyPolicy(selectedAccount)));
  }, [selectedAccount]);

  useEffect(() => {
    const firstItem = accountItems[0]?.item_id || '';
    if (!accountItems.some((item) => item.item_id === selectedItem)) {
      setSelectedItem(firstItem);
    }
  }, [accountItems, selectedItem]);

  useEffect(() => {
    if (!selectedAccount || !selectedItem) {
      setKnowledge([]);
      return;
    }
    getAIItemKnowledge(selectedAccount, selectedItem)
      .then(setKnowledge)
      .catch(() => setKnowledge([]));
  }, [selectedAccount, selectedItem]);

  const saveProvider = () =>
    run('provider-save', async () => {
      const saved = await saveAIProvider({
        api_key: apiKey,
        default_model: defaultModel,
      });
      setProvider(saved);
      setApiKey('');
      setNotice('DeepSeek 配置已加密保存');
    });

  const testProvider = () =>
    run('provider-test', async () => {
      const tested = await testDeepSeekConnection();
      setProvider(tested);
      setNotice('DeepSeek 连接测试成功');
    });

  const removeProvider = () =>
    run('provider-delete', async () => {
      await deleteAIProvider();
      const empty = await getAIProvider();
      setProvider(empty);
      setNotice('DeepSeek Key 已删除，相关账号 AI 已关闭');
    });

  const savePolicy = () => {
    if (!policy || !selectedAccount) return;
    run('policy-save', async () => {
      const { cookie_id: _cookieId, ...payload } = policy;
      const saved = await updateAIAccountPolicy(selectedAccount, payload);
      setPolicy(saved);
      setNotice('账号接待策略已保存');
    });
  };

  const saveKnowledge = () => {
    if (!selectedAccount || !selectedItem || !question.trim() || !answer.trim()) {
      setError('请先选择商品并填写问题和答案');
      return;
    }
    run('knowledge-save', async () => {
      const payload = {
        question: question.trim(),
        answer: answer.trim(),
        enabled: true,
        sort_order: 0,
      };
      if (editingKnowledgeId) {
        await updateAIItemKnowledge(editingKnowledgeId, payload);
      } else {
        await createAIItemKnowledge(selectedAccount, selectedItem, payload);
      }
      setKnowledge(await getAIItemKnowledge(selectedAccount, selectedItem));
      setQuestion('');
      setAnswer('');
      setEditingKnowledgeId(null);
      setNotice(editingKnowledgeId ? '商品问答已更新' : '商品问答已添加');
    });
  };

  const editKnowledge = (entry: AIItemKnowledge) => {
    setEditingKnowledgeId(entry.id);
    setQuestion(entry.question);
    setAnswer(entry.answer);
  };

  const removeKnowledge = (entry: AIItemKnowledge) =>
    run(`knowledge-delete-${entry.id}`, async () => {
      await deleteAIItemKnowledge(entry.id);
      setKnowledge(await getAIItemKnowledge(selectedAccount, selectedItem));
      setNotice('商品问答已删除');
    });

  const resolveHandoff = (entry: AIHandoff) =>
    run(`handoff-${entry.id}`, async () => {
      await resolveAIHandoff(entry.id);
      await refreshHandoffs();
      setNotice('会话已恢复 AI 接待');
    });

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-purple-600">
            <Bot className="h-4 w-4" /> 实用版智能客服
          </div>
          <h2 className="text-3xl font-black tracking-tight text-gray-900">
            AI 接待
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            先拦截风险，再使用知识库回答；异常时自动降级，不影响发货。
          </p>
        </div>
        <div className="rounded-2xl bg-orange-50 px-4 py-3 text-sm font-bold text-orange-700">
          待人工处理 {handoffs.length} 条
        </div>
      </div>

      {(notice || error) && (
        <div
          className={`rounded-2xl px-4 py-3 text-sm font-bold ${
            error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'
          }`}
        >
          {error || notice}
        </div>
      )}

      <section className={cardClass}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-xl font-black">
              <KeyRound className="h-5 w-5 text-purple-600" /> 1. DeepSeek 配置
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              每个网站账号使用自己的 Key，服务端加密保存。
            </p>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-xs font-bold ${
              provider?.configured
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {provider?.configured
              ? `已配置，尾号 ${provider.key_suffix}`
              : '尚未配置'}
          </span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2 text-sm font-bold text-gray-700">
            <span>DeepSeek API Key</span>
            <input
              aria-label="DeepSeek API Key"
              className={inputClass}
              type="password"
              autoComplete="new-password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="输入新的 API Key（保存后不会回显）"
            />
          </label>
          <label className="space-y-2 text-sm font-bold text-gray-700">
            <span>默认模型</span>
            <select
              aria-label="默认 DeepSeek 模型"
              className={inputClass}
              value={defaultModel}
              onChange={(event) =>
                setDefaultModel(event.target.value as DeepSeekModel)
              }
            >
              <option value="deepseek-v4-flash">V4 Flash（推荐，快速省钱）</option>
              <option value="deepseek-v4-pro">V4 Pro（复杂问题）</option>
            </select>
          </label>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            className="ios-btn-primary rounded-xl px-5 py-3 text-sm"
            disabled={!apiKey.trim() || Boolean(busy)}
            onClick={saveProvider}
          >
            {busy === 'provider-save' ? '保存中…' : '保存 DeepSeek 配置'}
          </button>
          <button
            className="rounded-xl bg-gray-900 px-5 py-3 text-sm font-bold text-white"
            disabled={!provider?.configured || Boolean(busy)}
            onClick={testProvider}
          >
            测试连接
          </button>
          {provider?.configured && (
            <button
              className="rounded-xl bg-red-50 px-5 py-3 text-sm font-bold text-red-600"
              disabled={Boolean(busy)}
              onClick={removeProvider}
            >
              删除 Key
            </button>
          )}
        </div>
      </section>

      <section className={cardClass}>
        <h3 className="flex items-center gap-2 text-xl font-black">
          <ShieldCheck className="h-5 w-5 text-blue-600" /> 2. 账号接待策略
        </h3>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <label className="space-y-2 text-sm font-bold">
            <span>闲鱼账号</span>
            <select
              aria-label="闲鱼账号"
              className={inputClass}
              value={selectedAccount}
              onChange={(event) => setSelectedAccount(event.target.value)}
            >
              {accounts.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.remark || entry.nickname || entry.id}
                </option>
              ))}
            </select>
          </label>
          {policy && (
            <>
              <label className="space-y-2 text-sm font-bold">
                <span>账号模型</span>
                <select
                  aria-label="账号 DeepSeek 模型"
                  className={inputClass}
                  value={policy.model_name}
                  onChange={(event) =>
                    setPolicy({
                      ...policy,
                      model_name: event.target.value,
                    })
                  }
                >
                  <option value="deepseek-v4-flash">V4 Flash</option>
                  <option value="deepseek-v4-pro">V4 Pro</option>
                </select>
              </label>
              <label className="space-y-2 text-sm font-bold">
                <span>语气</span>
                <select
                  aria-label="回复语气"
                  className={inputClass}
                  value={policy.reply_tone}
                  onChange={(event) =>
                    setPolicy({
                      ...policy,
                      reply_tone: event.target.value as AIAccountPolicy['reply_tone'],
                    })
                  }
                >
                  <option value="friendly">友好</option>
                  <option value="professional">专业</option>
                  <option value="concise">简洁</option>
                </select>
              </label>
            </>
          )}
        </div>
        {policy && (
          <div className="mt-5 space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <label className="flex items-center gap-3 rounded-2xl bg-gray-50 p-4 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={policy.ai_enabled}
                  onChange={(event) =>
                    setPolicy({ ...policy, ai_enabled: event.target.checked })
                  }
                />
                启用 DeepSeek 接待
              </label>
              <label className="space-y-2 text-sm font-bold">
                <span>最长回复字数</span>
                <input
                  aria-label="最长回复字数"
                  className={inputClass}
                  type="number"
                  min={20}
                  max={300}
                  value={policy.max_reply_chars}
                  onChange={(event) =>
                    setPolicy({
                      ...policy,
                      max_reply_chars: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label className="space-y-2 text-sm font-bold">
                <span>最多议价轮数</span>
                <input
                  aria-label="最多议价轮数"
                  className={inputClass}
                  type="number"
                  min={0}
                  max={20}
                  value={policy.max_bargain_rounds}
                  onChange={(event) =>
                    setPolicy({
                      ...policy,
                      max_bargain_rounds: Number(event.target.value),
                    })
                  }
                />
              </label>
            </div>
            <label className="block space-y-2 text-sm font-bold">
              <span>账号通用知识</span>
              <textarea
                aria-label="账号通用知识"
                className={`${inputClass} min-h-28`}
                value={policy.account_knowledge}
                onChange={(event) =>
                  setPolicy({ ...policy, account_knowledge: event.target.value })
                }
                placeholder="例如：工作日 18 点前发货；售后处理范围；沟通语气。"
              />
            </label>
            <label className="block space-y-2 text-sm font-bold">
              <span>追加风险词（每行一个）</span>
              <textarea
                aria-label="追加风险词"
                className={`${inputClass} min-h-20`}
                value={policy.risk_keywords.join('\n')}
                onChange={(event) =>
                  setPolicy({
                    ...policy,
                    risk_keywords: event.target.value
                      .split('\n')
                      .map((value) => value.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="内置退款、投诉、安全、违规、转人工规则不可删除"
              />
            </label>
            <button
              className="flex items-center gap-2 rounded-xl bg-black px-5 py-3 text-sm font-bold text-white"
              disabled={Boolean(busy)}
              onClick={savePolicy}
            >
              <Save className="h-4 w-4" /> 保存账号策略
            </button>
          </div>
        )}
      </section>

      <section className={cardClass}>
        <h3 className="flex items-center gap-2 text-xl font-black">
          <Bot className="h-5 w-5 text-indigo-600" /> 3. 商品知识库
        </h3>
        <div className="mt-5">
          <label className="block max-w-xl space-y-2 text-sm font-bold">
            <span>商品</span>
            <select
              aria-label="知识库商品"
              className={inputClass}
              value={selectedItem}
              onChange={(event) => setSelectedItem(event.target.value)}
            >
              {accountItems.length === 0 && <option value="">暂无同步商品</option>}
              {accountItems.map((entry) => (
                <option key={entry.item_id} value={entry.item_id}>
                  {entry.item_title || entry.item_id}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-[1fr_1.5fr_auto]">
          <input
            aria-label="知识库问题"
            className={inputClass}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="买家可能怎么问？"
          />
          <input
            aria-label="知识库答案"
            className={inputClass}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            placeholder="希望 AI 依据什么回答？"
          />
          <button
            className="flex items-center justify-center gap-2 rounded-xl bg-[#FFE815] px-5 py-3 text-sm font-black"
            disabled={Boolean(busy) || !selectedItem}
            onClick={saveKnowledge}
          >
            <Plus className="h-4 w-4" />
            {editingKnowledgeId ? '保存修改' : '添加问答'}
          </button>
        </div>
        <div className="mt-5 space-y-3">
          {knowledge.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <div className="font-bold text-gray-900">{entry.question}</div>
                <div className="mt-1 text-sm text-gray-600">{entry.answer}</div>
              </div>
              <div className="flex gap-2">
                <button
                  aria-label={`编辑问答 ${entry.question}`}
                  className="rounded-lg bg-white px-3 py-2 text-xs font-bold"
                  onClick={() => editKnowledge(entry)}
                >
                  编辑
                </button>
                <button
                  aria-label={`删除问答 ${entry.question}`}
                  className="rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-600"
                  onClick={() => removeKnowledge(entry)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {knowledge.length === 0 && (
            <p className="rounded-2xl bg-gray-50 p-5 text-center text-sm text-gray-400">
              当前商品还没有专属问答。
            </p>
          )}
        </div>
      </section>

      <section className={cardClass}>
        <h3 className="flex items-center gap-2 text-xl font-black">
          <UserRoundCheck className="h-5 w-5 text-orange-600" /> 4. 人工待办
        </h3>
        <div className="mt-5 space-y-3">
          {handoffs.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-col gap-4 rounded-2xl border border-orange-100 bg-orange-50/60 p-5 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <div className="flex items-center gap-2 font-black text-gray-900">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  {entry.reason_text}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {entry.buyer_name || entry.buyer_id || '未知买家'} · 商品{' '}
                  {entry.item_id || '未知'} · 账号 {entry.cookie_id}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  {entry.message_excerpt} · {entry.created_at}
                </div>
              </div>
              <button
                aria-label={`已接管并恢复 AI ${entry.buyer_name || entry.buyer_id}`}
                className="flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 text-sm font-bold text-white"
                disabled={Boolean(busy)}
                onClick={() => resolveHandoff(entry)}
              >
                <CheckCircle2 className="h-4 w-4" /> 已接管 / 恢复 AI
              </button>
            </div>
          ))}
          {handoffs.length === 0 && (
            <p className="rounded-2xl bg-green-50 p-5 text-center text-sm font-bold text-green-700">
              当前没有待人工处理的会话。
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AIReception;
